import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from "@mui/material";

/**
 * The one confirmation box for anything destructive (delete / remove).
 *
 *   <ConfirmDialog open={!!target} title="Delete test?" message="…" confirmLabel="Delete test"
 *                  onConfirm={() => deleteTest(target.id)} onClose={() => setTarget(null)} />
 *
 * requireText (optional): the user must type it (case/padding-insensitive) before the button enables - for
 * things that can't be undone and are easy to hit by mistake, like deleting a whole class.
 *
 * onConfirm may be async. While it runs both buttons are disabled; on success the dialog calls onClose;
 * on failure it stays open and shows the server's reason so the user can read it or cancel.
 */
export default function ConfirmDialog({
  open, title, message, confirmLabel = "Delete", busyLabel = "Deleting…", cancelLabel = "Cancel", danger = true, requireText, onConfirm, onClose,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [typed, setTyped] = useState("");
  const mounted = useRef(true);

  // Set true on every mount too: StrictMode (dev) runs cleanup once right after the first mount, which would
  // otherwise leave this false for good and the dialog would never close after a successful confirm.
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // A fresh open starts clean (a previous failure shouldn't linger).
  useEffect(() => { if (open) { setError(null); setBusy(false); setTyped(""); } }, [open]);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      if (mounted.current) { setBusy(false); onClose(); }
    } catch (e) {
      if (mounted.current) {
        setBusy(false);
        setError(e?.response?.data?.message || e?.response?.data?.error || "Something went wrong. Please try again.");
      }
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
        {requireText && (
          <TextField
            fullWidth size="small" autoFocus sx={{ mt: 2 }} value={typed} disabled={busy}
            label={`Type "${requireText}" to confirm`}
            onChange={(e) => setTyped(e.target.value)}
          />
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>{cancelLabel}</Button>
        <Button
          variant="contained"
          color={danger ? "error" : "primary"}
          onClick={handleConfirm}
          disabled={busy || (!!requireText && typed.trim().toLowerCase() !== requireText.trim().toLowerCase())}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {busy ? busyLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
